import { useMemo } from "react";

import * as Yup from "yup";

import { AmbulanzType } from "@/api";
import { EmailPattern, PhonePattern } from "@/common/helper/validationPatterns";
import { useTranslation } from "@/translations";

import { AmbulanzFormValues, AmbulanzMaxLength, AmbulanzTypeOption } from "../AmbulanzForm.types";

export function useAmbulanzFormOptions() {
  const { t } = useTranslation();

  const typeOptions = useMemo<AmbulanzTypeOption[]>(
    () => [
      { id: AmbulanzType.Ambulanz, label: t("ambulanzes.types.ambulanz") },
      { id: AmbulanzType.Callback, label: t("ambulanzes.types.callback") },
      { id: AmbulanzType.Sms, label: t("ambulanzes.types.sms") },
    ],
    [t]
  );

  const validationSchema = useMemo(() => {
    return Yup.object().shape<{ [Property in keyof AmbulanzFormValues]?: Yup.AnySchema }>({
      type: Yup.mixed<AmbulanzType>()
        .oneOf(Object.values(AmbulanzType))
        .required(
          t("common.validation.fieldRequired", {
            fieldName: t("ambulanzes.creationDialog.art"),
          })
        ),
      ambulanzNumber: Yup.string().when("type", {
        is: AmbulanzType.Ambulanz,
        then: (schema) =>
          schema
            .required(
              t("common.validation.fieldRequired", {
                fieldName: t("ambulanzes.creationDialog.ambulanzNumber"),
              })
            )
            .max(AmbulanzMaxLength.ambulanzNumber, ({ max }) => t("common.validation.maxLength", { max })),
        otherwise: (schema) => schema.optional(),
      }),
      clientName: Yup.string()
        .max(AmbulanzMaxLength.clientName, ({ max }) => t("common.validation.maxLength", { max }))
        .nullable(),
      phone: Yup.string()
        .max(AmbulanzMaxLength.phone, ({ max }) => t("common.validation.maxLength", { max }))
        .matches(PhonePattern, {
          message: t("ambulanzes.creationDialog.invalidPhone"),
          excludeEmptyString: true,
        }),
      email: Yup.string()
        .max(AmbulanzMaxLength.email, ({ max }) => t("common.validation.maxLength", { max }))
        .matches(EmailPattern, {
          message: t("ambulanzes.creationDialog.invalidEmail"),
          excludeEmptyString: true,
        }),
      note: Yup.string().max(AmbulanzMaxLength.note, ({ max }) => t("common.validation.maxLength", { max })),
    });
  }, [t]);

  return { typeOptions, validationSchema };
}
